import { Observable, catchError, of, share } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ServiceResult } from '@shared/helper/service-result';
import { TServiceResult } from '@shared/helper/t-service-result';
import { LocalRepository } from '@shared/helper/local-repository';
import { HttpBaseService } from '../base_services/http_base.service';
import { FollowsModel } from '@shared/model/followModels/follow-model';
import { PageList } from '@shared/model/categoryAndSearch/pageList-model';
import { TermFilterModel } from '@shared/model/categoryAndSearch/term-filter-model';
import { FollowStateEnum } from '@shared/model/enum/follow-state-enum';

@Injectable({
  providedIn: 'root'
})

export class FollowsService extends HttpBaseService {

  constructor() {
    const repo = inject(LocalRepository);
    const http = inject(HttpClient);

    super(repo, http, 'fo');
  }

  follow(follow: FollowsModel): Observable<ServiceResult> {
    return this.http.post<ServiceResult>(`${this.apiUrl}/Follow`, follow);
  }

  unFollow(unFollow: FollowsModel): Observable<TServiceResult<FollowStateEnum>> {
    return this.http.post<TServiceResult<FollowStateEnum>>(`${this.apiUrl}/UnFollow`, unFollow);
  }

  getFollowsState(id: string): Observable<TServiceResult<number>> {
    return this.http.get<TServiceResult<number>>(`${this.apiUrl}/GetFollowState/${id}`);
  }

  getMyFollowersCount(): Observable<TServiceResult<number>> {
    return this.http.get<TServiceResult<number>>(`${this.apiUrl}/GetMyFollowersCount`);
  }

  getMyFollowingsCount(): Observable<TServiceResult<number>> {
    return this.http.get<TServiceResult<number>>(`${this.apiUrl}/GetMyFollowingsCount`);
  }

  getMyFollowersList(): Observable<TServiceResult<PageList<FollowsModel>>> {
    return this.http.get<TServiceResult<PageList<FollowsModel>>>(`${this.apiUrl}/GetMyFollowersList`);
  }

  getMyFollowingsList(TF?: TermFilterModel): Observable<TServiceResult<PageList<FollowsModel>>> {
    let paramsValues = '';
    if (TF?.pgNumber || TF?.pgNumber == 0)
      paramsValues = `?PgNumber=${TF?.pgNumber}`
    if (TF?.pgSize)
      paramsValues = `?PgSize=${TF?.pgSize}`
    if ((TF?.pgNumber || TF?.pgNumber == 0) && TF?.pgSize)
      paramsValues = `?PgNumber=${TF?.pgNumber}&PgSize=${TF?.pgSize}`;
    return this.http.get<TServiceResult<PageList<FollowsModel>>>(`${this.apiUrl}/GetMyFollowingsList/${paramsValues}`);
  }

  getGuestFollowersCount(userId: string): Observable<TServiceResult<number>> {
    return this.http.get<TServiceResult<number>>(`${this.apiUrl}/GetGuestFollowersCount/${userId}`)
  }

  getGuestFollowingCount(userId: string): Observable<TServiceResult<number>> {
    return this.http.get<TServiceResult<number>>(`${this.apiUrl}/GetGuestFollowingsCount/${userId}`)
      .pipe(
        share(),
        catchError(() => {
          return of(new TServiceResult<number>(0, '', null, true, ''))
        })
      )
  }

  getGuestFollowersList(userId: string): Observable<TServiceResult<PageList<FollowsModel>>> {
    return this.http.get<TServiceResult<PageList<FollowsModel>>>(`${this.apiUrl}/GetGuestFollowersList/${userId}`);
  }

  getGuestFollowingList(userId: string): Observable<TServiceResult<PageList<FollowsModel>>> {
    return this.http.get<TServiceResult<PageList<FollowsModel>>>(`${this.apiUrl}/GetGuestFollowingsList/${userId}`);
  }

  getMyFollowersListWithFollowState(TF?: TermFilterModel): Observable<TServiceResult<PageList<FollowsModel>>> {
    let paramsValues = '';
    if (TF?.pgNumber || TF?.pgNumber == 0)
      paramsValues = `?PgNumber=${TF?.pgNumber}`
    if (TF?.pgSize)
      paramsValues = `?PgSize=${TF?.pgSize}`
    if ((TF?.pgNumber || TF?.pgNumber == 0) && TF?.pgSize)
      paramsValues = `?PgNumber=${TF?.pgNumber}&PgSize=${TF?.pgSize}`;
    return this.http.get<TServiceResult<PageList<FollowsModel>>>(`${this.apiUrl}/GetMyFollowersListWithFollowState/${paramsValues}`);
  }

  deleteFollowRequest(userId: string): Observable<TServiceResult<FollowStateEnum>> {
    return this.http.delete<TServiceResult<FollowStateEnum>>(`${this.apiUrl}/DeleteFollowRequest/${userId}`);
  }

  acceptFollowRequest(requesterId: string): Observable<TServiceResult<number>> {
    return this.http.patch<TServiceResult<number>>(`${this.apiUrl}/AcceptFollowRequest/${requesterId}`, null);
  }

  rejectFollowRequest(requesterId: string): Observable<TServiceResult<number>> {
    return this.http.delete<TServiceResult<number>>(`${this.apiUrl}/RejectFollowRequest/${requesterId}`);
  }

  removeFollower(followerId: string): Observable<TServiceResult<FollowStateEnum>> {
    return this.http.delete<TServiceResult<FollowStateEnum>>(`${this.apiUrl}/RemoveFollower/${followerId}`);
  }

}

export class FollowCountsModel {
  followers = 0;
  followings = 0;
  friends = 0
}
